import { Builder } from '@builder.io/react';
import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Text,
  TextProps,
} from '../../storyblocks-design-system/components/atoms/Text/Text';
import { createFontConfigInputs } from '../configurations/fontConfig';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';

interface AnimatedCounterProps
  extends Pick<TextProps, 'fontFamily' | 'fontWeight' | 'fontSize'> {
  /** How many units per second the counter should grow */
  growthRatio?: number;
  /** Determines whether the counter should continuously grow or animate to a static target */
  targetMode?: 'growth' | 'staticTarget';
  /** Target number to animate to when targetMode = 'staticTarget' */
  targetNumber?: number;
  /** Starting number */
  from?: number;
  /** Seconds to wait AFTER each animation completes before starting the next one */
  delay?: number;
  /** Duration in seconds of the animation itself */
  duration?: number;
  /** Thousands separator character */
  separator?: string;
  /** Mode that decides how the start value is calculated */
  mode?: 'static' | 'startDate' | 'currentMonth';
  /** ISO date string representing the moment the counter started (required if mode = 'startDate') */
  startDate?: string;
  /** Font color for the counter text */
  fontColor?: string;
  // Non-builder props
  className?: string;
  onStart?: () => void;
  onEnd?: () => void;
}

export default function AnimatedCounter({
  growthRatio,
  targetMode = 'growth',
  targetNumber,
  from = 0,
  delay = 3,
  duration = 2,
  className = '',
  separator = ',',
  mode = 'static',
  startDate,
  fontFamily = 'polysans',
  fontWeight = 'regular',
  fontSize = 'xl',
  fontColor,
  onStart,
  onEnd,
}: AnimatedCounterProps) {
  const { count, ref } = useAnimatedCounter({
    growthRatio,
    targetMode,
    targetNumber,
    from,
    delay,
    duration,
    mode,
    startDate,
    onStart,
    onEnd,
  });

  const formattedNumber = useMemo(() => {
    const number = Math.round(count);
    const formatter = new Intl.NumberFormat('en-US', {
      useGrouping: !!separator,
    });
    const formatted = formatter.format(number);
    return separator ? formatted.replace(/,/g, separator) : formatted;
  }, [count, separator]);

  return (
    <Text
      data-testid="animated-counter"
      asChild
      fontFamily={fontFamily}
      fontWeight={fontWeight}
      fontSize={fontSize}
      className={twMerge(
        'inline-block min-w-[3ch] text-right whitespace-nowrap tabular-nums',
        className
      )}
    >
      <span ref={ref} style={fontColor ? { color: fontColor } : undefined}>
        {formattedNumber}
      </span>
    </Text>
  );
}

Builder.registerComponent(AnimatedCounter, {
  name: 'Animated Counter',
  inputs: [
    {
      name: 'targetMode',
      friendlyName: 'Target mode',
      type: 'string',
      defaultValue: 'growth',
      enum: [
        { label: 'Growth', value: 'growth' },
        { label: 'Static target', value: 'staticTarget' },
      ],
      onChange: (options: any) => {
        const mode = options.get('targetMode');
        if (mode === 'growth') {
          options.set('targetNumber', undefined);
        } else if (mode === 'staticTarget') {
          options.set('growthRatio', undefined);
          options.set('delay', undefined);
        }
      },
    },
    {
      name: 'growthRatio',
      type: 'number',
      defaultValue: 600,
      helperText: 'Number of units the counter should grow per second',
      showIf: 'options.get("targetMode") === "growth"',
    },
    {
      name: 'targetNumber',
      type: 'number',
      defaultValue: 1000,
      helperText: 'Number the counter should animate to (static target mode)',
      showIf: 'options.get("targetMode") === "staticTarget"',
    },
    {
      name: 'mode',
      friendlyName: 'Starting value mode',
      type: 'string',
      defaultValue: 'static',
      enum: [
        { label: 'Static number', value: 'static' },
        { label: 'From start date', value: 'startDate' },
        { label: 'Current month', value: 'currentMonth' },
      ],
      onChange: (options: any) => {
        const mode = options.get('mode');
        if (mode === 'static') {
          options.set('startDate', undefined);
        } else if (mode === 'startDate') {
          options.set('from', undefined);
        } else if (mode === 'currentMonth') {
          options.set('from', undefined);
          options.set('startDate', undefined);
        }
      },
    },
    {
      name: 'from',
      type: 'number',
      defaultValue: 0,
      helperText: 'Starting number for the counter',
      showIf: 'options.get("mode") === "static"',
    },
    {
      name: 'startDate',
      type: 'date',
      helperText: 'Date to start accumulating value from',
      showIf: 'options.get("mode") === "startDate"',
    },
    {
      name: 'duration',
      type: 'number',
      defaultValue: 2,
      helperText: 'Duration in seconds for each growth animation',
    },
    {
      name: 'delay',
      type: 'number',
      defaultValue: 3,
      min: 3,
      helperText:
        'Seconds to wait after each animation completes before starting the next one (minimum of 3 seconds for readability).',
      showIf: 'options.get("targetMode") === "growth"',
    },
    ...createFontConfigInputs(),
    {
      name: 'fontColor',
      type: 'color',
      defaultValue: '#000000',
      helperText: 'Text color',
    },
  ],
});
